<template>
  <div>
    <the-breadcrumbs></the-breadcrumbs>
    <page-content page-title="Mein Profil bearbeiten">
      <support-organization-form
        :loading="loading"
        :organization="organization"
        :form-submitted="saveSupportOrganization"
        mode="edit"
      ></support-organization-form>

      <template v-if="showPretixCard">
        <el-divider></el-divider>

        <h3 style="margin-bottom: 20px;">Pretix-Verbindung</h3>
        <el-card v-loading="pretixLoading" style="max-width: 700px;">
          <el-row
            v-if="pretixStatus.connected"
            type="flex"
            justify="space-between"
            align="middle"
          >
            <el-col :span="16">
              <el-tag type="success" size="medium">
                <i class="el-icon-check"></i> Verbunden
              </el-tag>
              <div
                v-if="
                  pretixStatus.organizers && pretixStatus.organizers.length > 0
                "
                style="margin-top: 8px;"
              >
                <span
                  v-if="pretixStatus.organizers.length === 1"
                  style="color: #606266; font-size: 14px;"
                >
                  {{ pretixStatus.organizers[0].name }}
                </span>
                <el-select
                  v-else
                  :value="pretixStatus.organizer_slug"
                  placeholder="Organizer auswählen"
                  @change="onOrganizerChange"
                >
                  <el-option
                    v-for="org in pretixStatus.organizers"
                    :key="org.slug"
                    :label="org.name"
                    :value="org.slug"
                  ></el-option>
                </el-select>
              </div>
            </el-col>
            <el-col :span="8" style="text-align: right;">
              <el-button
                type="danger"
                plain
                :loading="pretixDisconnecting"
                @click="disconnectPretix"
              >
                Verbindung trennen
              </el-button>
            </el-col>
          </el-row>
          <el-row v-else type="flex" justify="space-between" align="middle">
            <el-col :span="16">
              <el-tag type="info" size="medium">
                <i class="el-icon-close"></i> Nicht verbunden
              </el-tag>
              <p style="margin: 8px 0 0; color: #606266; font-size: 14px;">
                Verbinden Sie Ihr Pretix-Konto, um Anmeldungen und
                Ticketverkäufe zu Ihren Veranstaltungen zu verwalten.
              </p>
            </el-col>
            <el-col :span="8" style="text-align: right;">
              <el-button
                type="primary"
                :loading="pretixConnecting"
                @click="connectPretix"
              >
                Mit Pretix verbinden
              </el-button>
            </el-col>
          </el-row>
        </el-card>
      </template>
    </page-content>
  </div>
</template>

<script>
import * as log from "loglevel"
import TheBreadcrumbs from "@/components/TheBreadcrumbs"
import PageContent from "@/components/PageContent"
import SupportOrganizationForm from "@/components/forms/SupportOrganizationForm"
import SupportOrganizationRepository from "@/repositories/organization/support_organizations_repository.js"
import PretixRepository from "@/repositories/pretix_repository.js"
import ApiErrorHandlerMixin from "@/mixins/api_error_handler.js"
import MessagesMixin from "@/mixins/messages.js"

export default {
  metaInfo: {
    title: "Profil bearbeiten"
  },
  components: {
    TheBreadcrumbs,
    PageContent,
    SupportOrganizationForm
  },
  mixins: [ApiErrorHandlerMixin, MessagesMixin],
  data() {
    return {
      loading: true,
      organization: {},
      pretixLoading: true,
      pretixConnecting: false,
      pretixDisconnecting: false,
      pretixStatus: {
        connected: false,
        token_valid: false,
        token_expires_at: null,
        organizer_slug: null,
        organizers: []
      }
    }
  },
  computed: {
    showPretixCard() {
      return !!this.organization.region_enable_pretix
    }
  },
  async created() {
    // Handle Pretix OAuth callback if code/state are present in URL
    const code = this.$route.query.code
    const state = this.$route.query.state
    if (code && state) {
      await this.handlePretixCallback(code, state)
    }

    try {
      this.organization = await SupportOrganizationRepository.get()
      this.loading = false
    } catch (error) {
      this.loading = false
      this.handleApiError(error)
    }

    if (this.showPretixCard) {
      this.loadPretixStatus()
    } else {
      this.pretixLoading = false
    }
  },
  methods: {
    async handlePretixCallback(code, state) {
      this.pretixLoading = true
      try {
        await PretixRepository.callback(code, state)
        // Clear query params from URL without reloading
        this.$router.replace({ path: this.$route.path })
        this.showCustomMessage(
          "Pretix-Konto wurde erfolgreich verbunden.",
          "success"
        )
      } catch (error) {
        // Clear query params from URL
        this.$router.replace({ path: this.$route.path })
        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          this.showCustomMessage(error.response.data.error, "error")
        } else {
          this.handleApiError(error)
        }
      }
    },
    async saveSupportOrganization(organization) {
      await SupportOrganizationRepository.update(organization)
    },
    async loadPretixStatus() {
      try {
        this.pretixStatus = await PretixRepository.status()
      } catch (error) {
        // Silently fail - user might not have permission
        log.warn("Could not load Pretix status:", error)
      } finally {
        this.pretixLoading = false
      }
    },
    async connectPretix() {
      this.pretixConnecting = true
      try {
        const authorizationUrl = await PretixRepository.connect()
        window.location.href = authorizationUrl
      } catch (error) {
        this.pretixConnecting = false
        this.handleApiError(error)
      }
    },
    async onOrganizerChange(slug) {
      try {
        await PretixRepository.update(slug)
        this.pretixStatus.organizer_slug = slug
        this.showCustomMessage(
          "Pretix Veranstalter wurde erfolgreich aktualisiert.",
          "success"
        )
      } catch (error) {
        this.handleApiError(error)
      }
    },
    async disconnectPretix() {
      try {
        await this.$confirm(
          "Möchten Sie die Verbindung zu Pretix wirklich trennen?",
          "Verbindung trennen",
          {
            confirmButtonText: "Trennen",
            cancelButtonText: "Abbrechen",
            type: "warning"
          }
        )
      } catch {
        return // User cancelled
      }

      this.pretixDisconnecting = true
      try {
        await PretixRepository.disconnect()
        this.pretixStatus = {
          connected: false,
          token_valid: false,
          token_expires_at: null,
          organizer_slug: null,
          organizers: []
        }
        this.showCustomMessage(
          "Pretix-Verbindung wurde erfolgreich getrennt.",
          "success"
        )
      } catch (error) {
        this.handleApiError(error)
      } finally {
        this.pretixDisconnecting = false
      }
    }
  }
}
</script>
